import React, { useState, useCallback, useEffect, useMemo } from "react";
import ReactDOM from "react-dom";
import {
  type NodeProps,
  type Node,
  Handle,
  Position,
  useReactFlow,
  useNodes,
  useEdges,
} from "@xyflow/react";
import {
  Mail,
  Trash2,
  Settings,
  X,
  AlertTriangle,
  Loader2,
  CheckCircle,
  MessageSquare,
  FileSearch,
} from "lucide-react";
import { getNodeValidationStatus } from "../../utils/nodeValidation";
import { NodeDataSidebar } from "../../common/shared/NodeDataSidebar";
import { AvailableVariable } from "../../common/ui/types";
import { getAllAncestorNodes } from "../../utils/workflowHelpers";
import { PromptEditorWithVariablesModal } from "../../common/ui/PromptEditorWithVariablesModal";
import NodeDocsButton from "../../docs/NodeDocsButton";

import syntaxHighlight from "../../utils/jsonViewer";

export const EmailParsingNode: React.FC<NodeProps<Node<any>>> = ({
  id,
  data,
  selected,
}) => {
  const [prompt, setPrompt] = useState<string>(data?.prompt || "");
  const [fileContext, setFileContext] = useState<string>(
    data?.file_context || "",
  );
  const [llmModelName, setLlmModelName] = useState<string>(
    data?.llm_model_name || "gemini-2.5-flash-lite",
  );
  const [processingType] = useState<string>("AI_OCR"); // Default as per request
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPromptModalOpen, setIsPromptModalOpen] = useState(false);
  const [, setDraggedVariable] = useState<string | null>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const { setNodes, deleteElements } = useReactFlow();
  const nodes = useNodes();
  const edges = useEdges();

  const hasOutputs = data?.outputs && Object.keys(data.outputs).length > 0;

  // Execution status - match summarizeNode pattern
  const executionStatus = data?.executionStatus || null;
  const isExecuting = executionStatus === "running";
  const executionSuccess = executionStatus === "success";
  const executionError = executionStatus === "error";

  const currentNode = useMemo(() => {
    return nodes.find((n) => n.id === id);
  }, [nodes, id]);

  const validationStatus = useMemo(() => {
    if (!currentNode) return { isValid: true, errors: [] };
    return getNodeValidationStatus(currentNode, edges, nodes);
  }, [currentNode, edges, nodes]);

  const stopPropagation = useCallback(
    (e: React.MouseEvent | React.FocusEvent) => {
      e.stopPropagation();
    },
    [],
  );

  const handleDeleteNode = useCallback(() => {
    deleteElements({ nodes: [{ id }] });
  }, [deleteElements, id]);

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleDeleteNode();
  };

  const handleOpenModal = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const extractNodeData = useCallback((nodeData: any) => {
    if (!nodeData || typeof nodeData !== "object")
      return { inputs: {}, outputs: {} };

    const inputs: Record<string, any> = {};
    let outputs: any = {};

    if (nodeData.outputs && typeof nodeData.outputs === "object") {
      outputs = nodeData.outputs;
      Object.keys(nodeData).forEach((key) => {
        if (
          key === "outputs" ||
          key.startsWith("_") ||
          key === "id" ||
          key === "executionStatus"
        )
          return;
        inputs[key] = nodeData[key];
      });
    } else {
      Object.keys(nodeData).forEach((key) => {
        if (key.startsWith("_") || key === "id") return;
        const value = nodeData[key];
        if (typeof value === "string" && value.includes(".outputs.")) {
          inputs[key] = value;
        } else {
          outputs[key] = value;
        }
      });
    }

    return { inputs, outputs };
  }, []);

  const previousNodes = useMemo(() => {
    const ancestorNodes = getAllAncestorNodes(id, nodes, edges);

    return ancestorNodes.map((sourceNode) => {
      const { inputs, outputs } = extractNodeData(sourceNode.data);
      return {
        nodeId: sourceNode.id,
        nodeName: sourceNode.data?.name || sourceNode.id,
        nodeType: sourceNode.type || "unknown",
        inputs,
        outputs,
      };
    }) as AvailableVariable[];
  }, [id, nodes, edges, extractNodeData]);

  const handleDragStart = (
    e: React.DragEvent,
    variable: string,
    nodeName: string,
    type: "input" | "output",
  ) => {
    e.stopPropagation();
    const fullVariable =
      type === "output"
        ? variable
          ? `${nodeName}.outputs.${variable}`
          : `${nodeName}.outputs`
        : variable
          ? `${nodeName}.inputs.${variable}`
          : `${nodeName}.inputs`;

    setDraggedVariable(fullVariable);
    e.dataTransfer.effectAllowed = "copy";
    e.dataTransfer.setData("text/plain", fullVariable);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const variable = e.dataTransfer.getData("text/plain");
    if (variable) {
      setFileContext(variable);
    }
    setDraggedVariable(null);
    setIsDraggingOver(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
  };

  const statusConfig = useMemo(() => {
    if (isExecuting) {
      return { bg: "bg-blue-500", text: "Executing...", pulse: true };
    } else if (executionSuccess) {
      return {
        bg: "bg-green-500",
        text: "Executed Successfully",
        pulse: false,
      };
    } else if (executionError) {
      return { bg: "bg-red-500", text: "Execution Failed", pulse: false };
    }

    const hasConfig = fileContext;
    return hasConfig
      ? { bg: "bg-amber-500", text: "Configured", pulse: false }
      : { bg: "bg-gray-400", text: "Not Configured", pulse: false };
  }, [fileContext, isExecuting, executionSuccess, executionError]);

  // Auto-populate fileContext when Outlook or Gmail node is connected
  useEffect(() => {
    if (fileContext) return;

    const emailNode = previousNodes.find(
      (node) =>
        node.nodeType === "OUTLOOK-CONNECTOR" ||
        node.nodeType === "GMAIL_CONNECTOR",
    );

    if (emailNode) {
      const autoFileContext = `${emailNode.nodeName}.outputs`;
      setFileContext(autoFileContext);
    }
  }, [previousNodes, fileContext]);

  // Sync node data when config changes (but NOT during execution)
  useEffect(() => {
    // Skip updating during execution to avoid overwriting executionStatus
    if (isExecuting) return;

    setNodes((nds) =>
      nds.map((node) => {
        if (node.id !== id) return node;

        return {
          ...node,
          data: {
            ...node.data,
            file_processing_type: processingType,
            llm_model_name: llmModelName,
            prompt,
            file_context: fileContext,
          },
        };
      }),
    );
  }, [processingType, fileContext, prompt, id, setNodes, isExecuting]);

  return (
    <div
      className={`relative group bg-amber-50 border-2 rounded-xl p-4 min-w-[280px] max-w-[320px] shadow-sm transition-all ${
        !validationStatus.isValid
          ? "border-red-500 shadow-red-200"
          : isExecuting
            ? "border-blue-500 shadow-lg shadow-blue-200 animate-pulse"
            : executionSuccess
              ? "border-green-500 shadow-md shadow-green-200"
              : executionError
                ? "border-red-500 shadow-md shadow-red-200"
                : selected
                  ? "border-amber-500 shadow-md"
                  : "border-amber-300 hover:border-amber-400"
      }`}
    >
      {isExecuting && (
        <div className="absolute inset-0 bg-blue-500/10 backdrop-blur-[1px] rounded-xl flex items-center justify-center z-10">
          <div className="bg-white rounded-full p-3 shadow-lg">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
        </div>
      )}
      {executionSuccess && (
        <div className="absolute -top-3 -right-4 z-30 pointer-events-none">
          <div className="bg-green-500 rounded-full p-1.5 border-2 border-white">
            <CheckCircle className="w-4 h-4 text-white" />
          </div>
        </div>
      )}
      {executionError && !isExecuting && (
        <div className="absolute -top-3 -right-4 z-30 pointer-events-none">
          <div className="bg-red-500 rounded-full p-1.5 border-2 border-white">
            <AlertTriangle className="w-4 h-4 text-white" />
          </div>
        </div>
      )}

      <Handle
        type="target"
        position={Position.Left}
        className="w-3 h-3 bg-amber-600 border-2 border-white"
        style={{
          boxShadow: "0 0 0 4px rgba(245, 158, 11, 0.2)",
        }}
      />

      <div className="flex items-start justify-between mb-3 cursor-move">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 rounded-lg shadow-sm bg-amber-100">
            <Mail className="w-4 h-4 text-amber-700" />
          </div>
          <div>
            <h3 className="font-medium text-gray-800 text-sm">Email Parsing</h3>
            <div className="flex items-center gap-1.5 mt-0.5">
              <div
                className={`w-1.5 h-1.5 rounded-full ${statusConfig.bg} ${
                  statusConfig.pulse ? "animate-pulse" : ""
                }`}
              />
              <p className="text-xs text-gray-600">{statusConfig.text}</p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1">
          <NodeDocsButton nodeType="EMAIL-OCR" />
          <button
            onClick={handleDeleteClick}
            className="text-gray-500 hover:text-gray-700 p-1 rounded transition"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {!validationStatus.isValid && (
        <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-xs">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-red-900 mb-1">
                Configuration Required
              </p>
              <ul className="text-red-700 space-y-0.5 list-disc list-inside">
                {validationStatus.errors.map((error, idx) => (
                  <li key={idx}>{error}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="mb-3" onClick={stopPropagation}>
        <button
          onClick={handleOpenModal}
          className="w-full py-2.5 rounded-lg bg-amber-600 text-white text-xs font-medium flex justify-center gap-2 shadow-sm hover:bg-amber-700"
        >
          <Settings className="w-3.5 h-3.5" /> Configure Email Parsing
        </button>
      </div>

      {isModalOpen &&
        ReactDOM.createPortal(
          <div
            className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center p-4"
            onClick={handleCloseModal}
          >
            <div
              className="bg-white rounded-lg shadow-xl w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col"
              onClick={stopPropagation}
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-50 rounded">
                    <Mail className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-gray-900">
                      Configure Email Parsing
                    </h2>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Extract structured data from emails using AI
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex-1 overflow-hidden flex divide-x divide-gray-200">
                <div className="w-[340px] shrink-0 overflow-hidden">
                  <NodeDataSidebar
                    availableVariables={previousNodes}
                    onDragStart={handleDragStart}
                  />
                </div>
                <div className="flex-1 overflow-y-auto bg-white">
                  <div className="p-6">
                    {!validationStatus.isValid && (
                      <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-start gap-3">
                          <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <h4 className="text-sm font-semibold text-red-900 mb-2">
                              Configuration Issues
                            </h4>
                            <ul className="text-sm text-red-700 space-y-1 list-disc list-inside">
                              {validationStatus.errors.map((error, idx) => (
                                <li key={idx}>{error}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    )}

                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      Email Parsing Configuration
                    </h3>
                    <p className="text-sm text-gray-500 mb-6">
                      Define which email content to parse and how
                    </p>

                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          LLM Model Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={llmModelName}
                          onChange={(e) => setLlmModelName(e.target.value)}
                          placeholder="e.g., gemini-2.5-flash-lite"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all bg-gray-50"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email Content Source{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <p className="text-xs text-gray-500 mb-3">
                          Drag the output from an email connector
                          (Outlook/Gmail) here.
                        </p>
                        <div
                          onDrop={handleDrop}
                          onDragOver={handleDragOver}
                          onDragLeave={handleDragLeave}
                          className={`min-h-[100px] px-4 py-4 border-2 border-dashed rounded-lg text-sm transition-all flex items-center justify-center ${
                            isDraggingOver
                              ? "border-amber-500 bg-amber-50 shadow-inner"
                              : fileContext
                                ? "border-green-400 bg-green-50/50"
                                : "border-gray-300 bg-gray-50 hover:border-amber-400"
                          }`}
                        >
                          {fileContext ? (
                            <div className="flex items-center justify-between gap-3 w-full">
                              <div className="flex items-center gap-4">
                                <div className="p-2 bg-green-100 rounded-full">
                                  <FileSearch className="w-5 h-5 text-green-600" />
                                </div>
                                <div>
                                  <div className="text-[10px] font-bold text-green-600 uppercase tracking-tight">
                                    Source Selected
                                  </div>
                                  <code className="text-xs text-green-800 font-mono break-all bg-white px-2 py-0.5 rounded border border-green-200 mt-1 block">
                                    {fileContext}
                                  </code>
                                </div>
                              </div>
                              <button
                                onClick={() => setFileContext("")}
                                className="text-gray-400 hover:text-red-600 p-2 hover:bg-white rounded-full transition-colors"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <div className="text-center">
                              <Mail className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                              <span className="text-sm font-medium text-gray-500">
                                Drop Email Context Here
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Parsing Prompt
                        </label>
                        <div
                          onClick={() => setIsPromptModalOpen(true)}
                          className="w-full min-h-[150px] p-4 border border-gray-300 rounded-lg text-sm text-gray-700 bg-gray-50 hover:bg-white hover:border-amber-400 cursor-pointer transition-all group relative"
                        >
                          {prompt ? (
                            <div className="whitespace-pre-wrap break-words font-mono text-xs line-clamp-6">
                              {prompt}
                            </div>
                          ) : (
                            <div className="flex flex-col items-center justify-center h-full text-gray-400 py-8">
                              <MessageSquare className="w-8 h-8 mb-2 opacity-50" />
                              <span className="text-sm font-medium">
                                Click to edit parsing prompt
                              </span>
                              <span className="text-xs mt-1 text-center">
                                Define what information should be extracted from
                                the email
                              </span>
                            </div>
                          )}
                          <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                            <div className="px-2 py-1 bg-amber-100 text-amber-600 rounded text-[10px] font-semibold">
                              Edit Prompt
                            </div>
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          Tip: You can use variables like{" "}
                          {"{{Source.outputs.email.subject}}"} in your prompt.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="w-80 bg-gray-50 overflow-y-auto">
                  <div className="p-4">
                    <div className="mb-4">
                      <h3 className="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        Node Outputs
                      </h3>
                      <p className="text-xs text-gray-500">
                        Available after execution
                      </p>
                    </div>

                    {hasOutputs ? (
                      <div className="space-y-3">
                        {Object.entries(data.outputs).map(
                          ([key, value]: [string, any]) => (
                            <div
                              key={key}
                              className="bg-white border border-gray-200 rounded-lg p-3 hover:border-green-300 transition-all"
                            >
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                  <span className="text-sm font-semibold text-gray-900">
                                    {key}
                                  </span>
                                </div>
                                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded font-mono">
                                  {typeof value}
                                </span>
                              </div>
                              <pre
                                className="text-xs font-mono break-all whitespace-pre-wrap"
                                dangerouslySetInnerHTML={{
                                  __html: syntaxHighlight(value),
                                }}
                              />
                            </div>
                          ),
                        )}
                      </div>
                    ) : (
                      <div className="p-6 bg-white border border-gray-200 rounded-lg text-center">
                        <div className="w-12 h-12 mx-auto mb-3 bg-gray-100 rounded-full flex items-center justify-center">
                          <Mail className="w-6 h-6 text-gray-400" />
                        </div>
                        <p className="text-sm text-gray-600 font-medium mb-1">
                          No outputs yet
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
                <div className="flex items-center gap-2">
                  {validationStatus.isValid ? (
                    <div className="flex items-center gap-2 text-sm text-green-700">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="font-medium">Configuration Valid</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-sm text-red-700">
                      <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                      <span className="font-medium">
                        {validationStatus.errors.length} issue(s) found
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleCloseModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCloseModal}
                    disabled={!fileContext}
                    className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50"
                  >
                    Save Configuration
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}

      <PromptEditorWithVariablesModal
        isOpen={isPromptModalOpen}
        onClose={() => setIsPromptModalOpen(false)}
        value={prompt}
        onChange={setPrompt}
        title="Edit Email Parsing Prompt"
        description="Write instructions on how to parse the email content."
        placeholder="e.g., Check if this email subject is related to an invoice. If yes, extract invoice number and amount."
        availableVariables={previousNodes}
      />

      <Handle
        type="source"
        position={Position.Right}
        className="w-3 h-3 bg-amber-600 border-2 border-white"
        style={{
          boxShadow: "0 0 0 4px rgba(245, 158, 11, 0.2)",
        }}
      />
    </div>
  );
};

export default EmailParsingNode;
